"use client";

import { useState, type ReactNode } from "react";

interface ScreenshotImageProps {
  src: string;
  alt: string;
  className?: string;
  loading?: "lazy" | "eager";
  /** Rendered in place of the image once it has failed twice. */
  fallback?: ReactNode;
}

/**
 * Screenshot <img> that never leaves a broken image behind.
 *
 * Most screenshot load failures are transient (an aborted request, a cold R2
 * read), so the first error retries once with a cache-busting param. A second
 * failure means the object is genuinely missing — render the fallback instead.
 */
export default function ScreenshotImage({
  src,
  alt,
  className,
  loading,
  fallback = null,
}: ScreenshotImageProps) {
  const [attempt, setAttempt] = useState(0);

  if (attempt > 1) return <>{fallback}</>;

  return (
    <img
      src={attempt === 0 ? src : `${src}?retry=1`}
      alt={alt}
      className={className}
      loading={loading}
      onError={() => setAttempt((a) => a + 1)}
    />
  );
}
